import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, getResizedImageUrl } from '../lib/cms';
import TestimonialSlider from '../components/TestimonialSlider';
import ContactForm from '../components/ContactForm';
import ContentWithCTA from '../components/ContentWithCTA';

export default function Forside() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchPageBySlug('forside');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  // Focus boxes configuration
  const focusBoxes = [
    {
      title: 'Belægning',
      slug: 'belaegning',
      path: '/ydelser/belaegning',
      icon: 'fa-solid fa-border-all',
      desc: 'Professionel flisebelægning, indkørsler, terrasser og støttemure i højeste kvalitet.',
      bgImage: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-house-modern-landscaped-garden-driveway.jpg'
    },
    {
      title: 'Entreprenør',
      slug: 'entreprenoer',
      path: '/ydelser/entreprenoer',
      icon: 'fa-solid fa-helmet-safety',
      desc: 'Jord-, grave- og entreprenørarbejde udført effektivt med en moderne maskinpark.',
      bgImage: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-excavator-construction-site.jpg'
    },
    {
      title: 'Støbearbejde',
      slug: 'stoebearbejde',
      path: '/ydelser/stoebearbejde',
      icon: 'fa-solid fa-layer-group',
      desc: 'Sikre og stabile fundamenter støbt efter alle gældende krav og standarder.',
      bgImage: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-patio-stone-retaining-wall.jpg'
    },
    {
      title: 'Betonarbejde',
      slug: 'betonarbejde',
      path: '/ydelser/betonarbejde',
      icon: 'fa-solid fa-fill-drip',
      desc: 'Klassisk betonarbejde, støbning af gulve, sokler og slidstærke betonløsninger.',
      bgImage: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-masonry-bricklayer-working.jpg'
    },
    {
      title: 'Murerarbejde',
      slug: 'murerarbejde',
      path: '/ydelser/murerarbejde',
      icon: 'fa-solid fa-trowel-bricks',
      desc: 'Klassisk murerhåndværk, opmuring, flisearbejde og renovering af facader.',
      bgImage: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/danish-masonry-bricklayer-working.jpg'
    },
    {
      title: 'Totalløsninger',
      slug: 'totalloesninger',
      path: '/ydelser/totalloesninger',
      icon: 'fa-solid fa-screwdriver-wrench',
      desc: 'Vi tager ansvaret for hele dit projekt – fra første spadestik til færdigt byggeri.',
      bgImage: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/image00001-min-scaled_4.webp'
    },
    {
      title: 'Anlægsarbejde',
      slug: 'anlaegsarbejde',
      path: '/ydelser/anlaegsarbejde',
      icon: 'fa-solid fa-seedling',
      desc: 'Etablering af haver, rullegræs, plantebede og professionel træfældning.',
      bgImage: 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/nyanlaeg-af-have-i-skive_6.jpg'
    },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#163A2B] border-t-transparent"></div>
      </div>
    );
  }

  const heroBg = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/dji-20260630142228-0025-d.jpg';
  const resizedHeroBg = getResizedImageUrl(heroBg, 1920, 85);

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* Hero Section */}
      <section className="relative bg-[#1C1E1D] py-24 sm:py-32 lg:py-40 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={resizedHeroBg}
            alt="Larsen & Kristensen Byggeplads"
            className="h-full w-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1E1D] via-[#1C1E1D]/40 to-transparent"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#163A2B] px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#F4F1EA] mb-6">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
              To stærke kræfter er fusioneret
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] font-display">
              Solidt håndværk &<br />
              <span className="text-[#163A2B] bg-[#F4F1EA] px-2 py-0.5 rounded-md inline-block mt-2">totalløsninger</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[#F4F1EA]/90 leading-relaxed max-w-2xl">
              {pageData?.intro || 'Anlægsfirmaet K. Larsen og LHK byg og bolig slog tøjlerne sammen for at tilbyde dig stærke totalløsninger under ét tag.'}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-md bg-[#163A2B] px-6 py-3.5 text-base font-semibold text-white shadow-md hover:bg-white hover:text-[#1C1E1D] transition-all duration-200 cursor-pointer"
              >
                <i className="fa-solid fa-envelope mr-2" /> Kontakt os
              </Link>
              <a
                href="#fokus-omraader"
                className="inline-flex items-center justify-center rounded-md border-2 border-white/80 bg-transparent px-6 py-3.5 text-base font-semibold text-white hover:bg-white hover:text-[#1C1E1D] hover:border-white transition-all duration-200 cursor-pointer"
              >
                Se vores ydelser <i className="fa-solid fa-arrow-right ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Intro Text */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3 py-1.5 rounded-full">
                Hvem er vi
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1E1D] font-display leading-tight">
                Anlægsfirmaet K. Larsen og LHK byg og bolig slog tøjlerne sammen...
              </h2>
              <ContentWithCTA 
                html={pageData?.content_html} 
                className="text-base text-[#1C1E1D]/80 leading-relaxed space-y-4"
              />
              <div className="pt-4">
                <Link
                  to="/om-larsen-kristensen"
                  className="inline-flex items-center text-sm font-bold text-[#163A2B] hover:text-[#1C1E1D] transition-colors group cursor-pointer"
                >
                  Læs hele historien om vores fusion 
                  <i className="fa-solid fa-arrow-right ml-1.5 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Intro Visual Badge / Image */}
            <div className="lg:col-span-5 relative">
              <div className="aspect-4/3 overflow-hidden rounded-lg border border-[#E2E2E0] shadow-md bg-[#163A2B]/10">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/l-k_1.jpg" 
                  alt="Larsen & Kristensen arbejde" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Floating Badge - NO hover effects as it is not clickable */}
              <div className="absolute -bottom-6 -left-6 bg-[#163A2B] text-white p-6 rounded-lg shadow-lg max-w-xs hidden sm:block border border-white/10">
                <p className="text-2xl font-bold font-display">100%</p>
                <p className="text-xs uppercase tracking-wider font-semibold text-[#F4F1EA]/80 mt-1">Lokalt forankret i Skive</p>
                <p className="text-xs text-[#F4F1EA]/70 mt-2">Vi leverer solidt jysk håndværk og professionelle totalløsninger i hele regionen.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Focus Boxes Section */}
      <section id="fokus-omraader" className="py-16 sm:py-24 bg-[#F4F1EA]/50 border-t border-[#E2E2E0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3 py-1.5 rounded-full">
              Vores Fokusområder
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1E1D] mt-4 font-display">
              Hvad kan vi hjælpe dig med?
            </h2>
            <p className="text-base text-[#1C1E1D]/70 mt-3">
              Vores fusion gør os i stand til at løse alle opgaver inden for byggeri, anlæg og beton. Klik på en ydelse nedenfor for at læse mere og se vores galleri.
            </p>
          </div>

          {/* 6 Focus Boxes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {focusBoxes.map((box) => (
              <Link
                key={box.slug}
                to={box.path}
                className="group relative flex flex-col justify-between overflow-hidden rounded-lg border border-[#E2E2E0] bg-white p-6 sm:p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:border-[#163A2B] cursor-pointer"
              >
                {/* Top content */}
                <div>
                  {/* Icon Badge */}
                  <div className="inline-flex items-center justify-center rounded-md bg-[#F4F1EA] text-[#163A2B] w-12 h-12 mb-6 transition-colors group-hover:bg-[#163A2B] group-hover:text-white">
                    <i className={`${box.icon} text-xl`} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#1C1E1D] group-hover:text-[#163A2B] transition-colors font-display">
                    {box.title}
                  </h3>
                  <p className="text-sm text-[#1C1E1D]/70 mt-3 leading-relaxed">
                    {box.desc}
                  </p>
                </div>

                {/* Bottom link indicator */}
                <div className="mt-6 pt-4 border-t border-[#E2E2E0] flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#163A2B] group-hover:text-[#1C1E1D] transition-colors">
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1.5" />
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialSlider />

      {/* Contact Section */}
      <section className="py-16 sm:py-24 bg-[#F4F1EA]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Contact Info Column */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-white px-3 py-1.5 rounded-full border border-[#E2E2E0]">
                  Kontakt os i dag
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1E1D] mt-4 font-display">
                  Lad os tage en snak om dit projekt
                </h2>
                <p className="text-base text-[#1C1E1D]/70 mt-3 leading-relaxed">
                  Uanset om du står med en mindre belægningsopgave eller et stort, komplekst byggeri, står vi klar med professionel rådgivning og et uforpligtende tilbud.
                </p>
              </div>

              {/* Contact Cards - NO hover effects as they are not clickable */}
              <div className="space-y-4">
                <div className="bg-white border border-[#E2E2E0] rounded-lg p-5 flex items-start gap-4 shadow-sm">
                  <div className="bg-[#F4F1EA] text-[#163A2B] p-3 rounded-md shrink-0">
                    <i className="fa-solid fa-phone text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Ring direkte</p>
                    <p className="text-lg font-bold text-[#1C1E1D] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <a href="tel:+4530713311" className="hover:underline whitespace-nowrap">30 71 33 11</a>
                      <span className="text-[#E2E2E0] hidden sm:inline">|</span>
                      <a href="tel:+4591968419" className="hover:underline whitespace-nowrap">91 96 84 19</a>
                    </p>
                    <p className="text-xs text-[#1C1E1D]/60 mt-1">Vi svarer alle hverdage 07:00 - 16:00</p>
                  </div>
                </div>

                <div className="bg-white border border-[#E2E2E0] rounded-lg p-5 flex items-start gap-4 shadow-sm">
                  <div className="bg-[#F4F1EA] text-[#163A2B] p-3 rounded-md shrink-0">
                    <i className="fa-solid fa-envelope text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Send en e-mail</p>
                    <p className="text-lg font-bold text-[#1C1E1D] mt-0.5">
                      <a href="mailto:kontakt@logkaps.dk" className="hover:underline whitespace-nowrap">kontakt@logkaps.dk</a>
                    </p>
                    <p className="text-xs text-[#1C1E1D]/60 mt-1">Vi besvarer e-mails inden for 24 timer</p>
                  </div>
                </div>

                <div className="bg-white border border-[#E2E2E0] rounded-lg p-5 flex items-start gap-4 shadow-sm">
                  <div className="bg-[#F4F1EA] text-[#163A2B] p-3 rounded-md shrink-0">
                    <i className="fa-solid fa-location-dot text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Besøg vores kontor</p>
                    <p className="text-lg font-bold text-[#1C1E1D] mt-0.5 whitespace-nowrap">Krarupvej 58, 7800 Skive</p>
                    <p className="text-xs text-[#1C1E1D]/60 mt-1">Lokalt forankret i Midt- og Vestjylland</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
